package utils

/**
 * Server-Sent Events (SSE) client for real-time updates
 *
 * Example usage:
 * ```kotlin
 * val sseClient = SseClient("http://localhost:3001/api/events")
 * sseClient.addEventListener("post.created") { event ->
 *     val data = JSON.parse<PostData>(event.data.toString())
 *     println("New post: ${data.title}")
 * }
 * sseClient.connect()
 * ```
 */

/**
 * Error types for better error handling
 */
enum class SseErrorType {
    TRANSIENT,   // Temporary network errors - should reconnect
    FATAL,       // Permanent errors (401, 404) - should not reconnect
    UNKNOWN      // Unknown error type
}

/**
 * Error information callback
 */
data class SseError(
    val type: SseErrorType,
    val message: String,
    val event: dynamic?
)

class SseClient(private val url: String) {

    private var eventSource: EventSource? = null
    private val listeners = mutableMapOf<String, (MessageEvent) -> Unit>()
    private var onErrorCallback: ((SseError) -> Unit)? = null
    private var shouldReconnect = true

    /**
     * Set error callback to be notified of connection errors
     */
    fun onError(callback: (SseError) -> Unit) {
        this.onErrorCallback = callback
    }

    /**
     * Connect to the SSE endpoint
     */
    fun connect() {
        if (eventSource != null) {
            console.warn("SSE client already connected")
            return
        }

        shouldReconnect = true

        try {
            eventSource = EventSource(url).apply {
                onopen = { e ->
                    console.log("SSE connected:", e)
                }

                onerror = { e ->
                    handleError(e)
                }

                onmessage = { e ->
                    // Default message handler for messages without event type
                    console.log("SSE message:", e)
                }
            }

            // Register all event listeners
            listeners.forEach { (eventType, callback) ->
                eventSource?.addEventListener(eventType) { e ->
                    val messageEvent = e.asMessageEvent()
                    if (messageEvent != null) {
                        callback(messageEvent)
                    }
                }
            }

            console.log("SSE client connecting to: $url")
        } catch (e: Throwable) {
            handleError(e)
        }
    }

    /**
     * Handle SSE errors with proper error type detection
     */
    private fun handleError(event: Any?) {
        console.error("SSE error:", event)

        val errorType = classifyError(event)
        val error = SseError(
            type = errorType,
            message = when (errorType) {
                SseErrorType.FATAL -> "Fatal SSE error - connection will not be retried"
                SseErrorType.TRANSIENT -> "Temporary SSE error - will reconnect"
                SseErrorType.UNKNOWN -> "Unknown SSE error"
            },
            event = event?.asDynamic()
        )

        // Notify error callback if registered
        onErrorCallback?.invoke(error)

        // Stop reconnection attempts for fatal errors
        if (errorType == SseErrorType.FATAL) {
            shouldReconnect = false
            disconnect()
        }
    }

    /**
     * Classify error type based on EventSource state
     * Note: EventSource doesn't expose HTTP status directly in JS API
     * We use readyState to infer error type
     */
    private fun classifyError(event: Any?): SseErrorType {
        val readyState = eventSource?.readyState

        return when {
            // EventSource.CLOSED or EventSource.CONNECTING with repeated failures
            // suggests a fatal error (authentication failed, endpoint not found, etc.)
            readyState == EventSourceConstants.CLOSED -> {
                // If closed and we didn't intentionally close it, it's likely fatal
                if (eventSource != null && shouldReconnect) {
                    SseErrorType.FATAL
                } else {
                    SseErrorType.TRANSIENT
                }
            }
            // EventSource.OPEN with error is likely transient
            readyState == EventSourceConstants.OPEN -> SseErrorType.TRANSIENT
            // CONNECTING state could be either - assume transient
            else -> SseErrorType.TRANSIENT
        }
    }

    /**
     * Add an event listener for a specific event type
     *
     * @param eventType - The event name (e.g., "post.created", "comment.created")
     * @param callback - The callback function to handle the event
     */
    fun addEventListener(eventType: String, callback: (MessageEvent) -> Unit) {
        listeners[eventType] = callback

        // If already connected, add the listener immediately
        eventSource?.addEventListener(eventType) { e ->
            val messageEvent = e.asMessageEvent()
            if (messageEvent != null) {
                callback(messageEvent)
            }
        }
    }

    /**
     * Remove an event listener
     *
     * @param eventType - The event name to remove
     */
    fun removeEventListener(eventType: String) {
        listeners.remove(eventType)
        // Note: EventSource doesn't support removing specific event listeners in JavaScript
        // We just remove it from our local map
    }

    /**
     * Disconnect from the SSE endpoint
     */
    fun disconnect() {
        eventSource?.close()
        eventSource = null
        listeners.clear()
        console.log("SSE client disconnected")
    }

    /**
     * Check if the client is currently connected
     */
    val isConnected: Boolean
        get() = eventSource?.readyState == EventSourceConstants.OPEN

    companion object {
        /**
         * Create an SSE client with the API base URL
         *
         * @param apiBaseUrl - The base URL of the API (e.g., "http://localhost:3001")
         * @param path - The SSE endpoint path (default: "/api/events")
         */
        fun create(apiBaseUrl: String, path: String = "/api/events"): SseClient {
            val fullUrl = if (apiBaseUrl.endsWith("/")) {
                "$apiBaseUrl${path.substring(1)}"
            } else {
                "$apiBaseUrl$path"
            }
            return SseClient(fullUrl)
        }
    }
}

/**
 * SSE Event data classes
 */
external interface PostCreatedData {
    val id: Int
    val title: String
    val content: String
    val authorId: Int
    val author: AuthorData
    val status: String
    val createdAt: String
}

external interface PostUpdatedData {
    val id: Int
    val title: String
    val status: String
    val updatedAt: String
}

external interface PostDeletedData {
    val id: Int
    val deletedAt: String
}

external interface CommentCreatedData {
    val id: Int
    val postId: Int
    val authorName: String
    val content: String
    val createdAt: String
}

external interface CommentDeletedData {
    val id: Int
    val postId: Int
    val deletedAt: String
}

external interface AuthorData {
    val id: Int
    val username: String
}

external interface ConnectedData {
    val connectionId: String
    val authenticated: Boolean
    val timestamp: String
}

external interface HeartbeatData {
    val timestamp: String
}
